import { useMemo, useState } from 'react'
import Styles from './AIRevision.module.scss'
import { usePollRevision } from 'hooks/queries/layers/revisions/usePollRevision'
import { Stepper } from 'components/AIRevision/Stepper/Stepper'
import { StepperProvider } from 'components/AIRevision/Stepper/StepperProvder'
import { AIRevisions } from 'components/AIRevision/AIRevisions'
import { Revision } from 'types'
import { RevisionType } from 'components/BlazeCommandLine/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  id: string
  articleId: string
  layerId: string
}

const collectRevisions = (revision: Revision, result: Revision[] = []): Revision[] => {
  result.push(revision)

  if (!revision.previous_revision) return result

  return collectRevisions(revision.previous_revision, result)
}

export const AIRevisionContainer = ({ id, articleId, layerId }: Props) => {
  const [customPrompt, setCustomPrompt] = useState('')

  const { data, isLoading } = usePollRevision({ articleId, layerId, id })

  if (isLoading || !data?.revision) {
    return null
  }

  const { revision } = data

  const revisionsArray = collectRevisions(revision).filter(Boolean).reverse() as Revision[]
  const revisionsCount = revisionsArray.length

  const handleStepChange = (step: number) => {
    const revision = revisionsArray[step - 1]
    if (step !== revisionsCount && revision.prompt.revision_type === RevisionType.CUSTOM_PROMPT) {
      setCustomPrompt(revision.prompt.custom_prompt)
    } else {
      setCustomPrompt('')
    }
  }

  return (
    <div className={Styles.container}>
      <StepperProvider steps={revisionsCount} initialStep={revisionsCount} onStepChange={handleStepChange}>
        <div className={Styles.revision}>
          <AIRevisions
            revisions={revisionsArray}
            articleId={articleId}
            layerId={layerId}
            customPrompt={customPrompt}
            onPromptChange={setCustomPrompt}
          />
        </div>
        {revisionsCount > 1 && (
          <div className={Styles.stepper}>
            <Stepper />
          </div>
        )}
      </StepperProvider>
    </div>
  )
}

interface FloatingProps extends Props {
  anchor: HTMLElement
}

export const FloatingAIRevisionContainer = ({ anchor, ...props }: FloatingProps) => {
  const computedBoundary = useMemo(() => anchor.closest('#editor-container') || undefined, [anchor])
  const containerSidePadding = 16
  const { isMobile } = useResponsiveDesign()

  const width = computedBoundary?.getBoundingClientRect().width || 0

  if (isMobile) return null

  return (
    <div
      style={{
        maxWidth: `${width + containerSidePadding * 2}px`,
        width: `${width + containerSidePadding * 2}px`,
        position: 'absolute',
        top: anchor.offsetTop + anchor.getBoundingClientRect().height,
        left: -containerSidePadding,
        ['--side-padding' as string]: `${containerSidePadding}px`,
      }}
    >
      <AIRevisionContainer {...props} />
    </div>
  )
}
